/* ===========================================================
   pages/Welcome.jsx — the one question, then a short tour

   "How will you use Docs Protocol?" → Casual or Developer (changeable
   any time in Settings; it only changes what's shown, never what's in
   a doc). Then Proto's four-card tour, skippable on every card, and
   an example doc made for the mode you picked.
   =========================================================== */

import { useState } from 'preact/hooks';
import { updateProfile, session } from '../state.js';
import { go } from '../lib/router.js';
import { createDoc } from '../sync/docs.js';
import { WELCOME_CASUAL, WELCOME_DEV } from '../templates.js';
import { Button, Icon, toast } from '../ui/ui.jsx';
import { Proto } from '../ui/proto.jsx';

const TOUR = [
  { title: 'Hi, I\'m Proto!', text: 'I look after your docs. This is a calm place to write: notes, school work, plans, anything.', icon: 'pencil' },
  { title: 'Everything saves by itself', text: 'Every word is saved on this device right away and synced to your account. No internet? Keep writing; it catches up later.', icon: 'cloud' },
  { title: 'Share like you\'re used to', text: 'The Share button makes a link that can view, or one that can edit. People editing at the same time see each other\'s cursors.', icon: 'share' },
  { title: 'Find anything fast', text: 'Press Ctrl K (or tap the magnifying glass) to jump to any doc, heading, or action. Version history keeps every draft safe.', icon: 'search' },
];

export function Welcome({ replay = false }) {
  const s = session.value;
  const [step, setStep] = useState(replay ? 1 : 0);   // 0 = the question, 1..4 = tour
  const [picked, setPicked] = useState(s.profile?.mode || null);
  const [busy, setBusy] = useState(false);

  const choose = async m => {
    setPicked(m);
    setStep(1);
    updateProfile({ mode: m }).catch(err => toast(err, { kind: 'error' }));
  };

  const finish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!s.profile?.onboarded || !replay) {
        const ex = picked === 'dev' ? WELCOME_DEV : WELCOME_CASUAL;
        const id = await createDoc({ title: ex.title, json: ex.json });
        updateProfile({ onboarded: true, mode: picked || 'casual' }).catch(err => toast(err, { kind: 'error' }));
        go(`/d/${id}`, { replace: true });
      } else {
        go('/', { replace: true });
      }
    } finally { setBusy(false); }
  };

  if (step === 0) {
    return (
      <div class="welcome">
        <div class="welcome-card wide">
          <Proto size={84} pose="wave" />
          <h1>How will you use Docs Protocol?</h1>
          <p class="lead">Pick one. You can switch any time in Settings, and nothing in your docs changes.</p>
          <div class="mode-choices">
            <button type="button" class="mode-choice" onClick={() => choose('casual')}>
              <Icon name="pencil" size={32} />
              <strong>Casual</strong>
              <span>Writing, notes, school, and everyday documents</span>
            </button>
            <button type="button" class="mode-choice" onClick={() => choose('dev')}>
              <Icon name="code" size={32} />
              <strong>Developer</strong>
              <span>Code, technical docs, and live examples</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const card = TOUR[step - 1];
  const last = step === TOUR.length;
  return (
    <div class="welcome">
      <div class="welcome-card" role="dialog" aria-label="Welcome tour" aria-live="polite">
        <Proto size={80} />
        <div class="tour-icon"><Icon name={card.icon} size={22} /></div>
        <h1>{card.title}</h1>
        <p class="lead">{card.text}</p>
        <div class="tour-dots" aria-hidden="true">{TOUR.map((_, i) => <span class={i === step - 1 ? 'on' : ''} />)}</div>
        <div class="tour-actions">
          {!last && <Button onClick={finish} disabled={busy}>Skip</Button>}
          <span class="grow" />
          {step > 1 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
          <Button kind="primary" autofocus onClick={() => (last ? finish() : setStep(step + 1))} disabled={busy}>
            {last ? (replay ? 'Done' : 'Open my example doc') : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
